// ═══════════════════════════════════════════════════════════════
// PLATFORM CORE RE-EXPORTS (IPayment interface and adapters)
// These provide the unified platform-core payment abstraction
// ═══════════════════════════════════════════════════════════════

export {
  // IPayment interface and types
  type IPayment,
  type PaymentIntent,
  type PaymentStatus,
  type PaymentMethod,
  type PaymentMethodType,
  type CardDetails,
  type CardBrand,
  type BankAccountDetails,
  type BillingDetails,
  type Address,
  type PaymentError,
  type PaymentErrorCode,
  type CreatePaymentIntentOptions,
  type UpdatePaymentIntentOptions,
  type ConfirmPaymentIntentOptions,
  type CapturePaymentIntentOptions,
  type Refund,
  type RefundStatus,
  type RefundReason,
  type CreateRefundOptions,
  type Transfer,
  type TransferStatus,
  type CreateTransferOptions,
  type Payout,
  type PayoutStatus,
  type CreatePayoutOptions,
  type ConnectedAccount,
  type AccountType,
  type BusinessProfile,
  type ConnectedAccountStatus,
  type CreateConnectedAccountOptions,
  type AccountCapabilities,
  type TosAcceptance,
  type CreateAccountLinkOptions,
  type AccountLink,
  type Customer,
  type CreateCustomerOptions,
  type UpdateCustomerOptions,
  type Balance,
  type BalanceAmount,
  type PaymentWebhookEvent,
  type PaymentEventType,
  type VerifyWebhookOptions,
  type PaymentConfig,
  // Helpers
  createPaymentError,
  isPaymentError,
  PaymentErrorMessages,
  formatAmount,
  generatePaymentId,
  // Memory adapter for testing
  MemoryPayment,
} from '@digilogiclabs/platform-core';

// Production adapters
export {
  StripePayment,
  type StripePaymentConfig,
} from '@digilogiclabs/platform-core';

// ═══════════════════════════════════════════════════════════════
// PLATFORM CORE INTEGRATION (observability)
// ═══════════════════════════════════════════════════════════════

export {
  setPlatform,
  clearPlatform,
  hasPlatform,
  logPaymentEvent,
  trackPaymentMetric,
  trackRevenue,
  trackCheckoutSession,
  trackSubscriptionEvent,
  trackWebhookEvent,
  cacheSubscriptionStatus,
  getCachedSubscriptionStatus,
} from './platform/index.js';

// ═══════════════════════════════════════════════════════════════
// CORE SHARED EXPORTS (safe for server-side)
// ═══════════════════════════════════════════════════════════════

export * from './shared/types';
export * from './shared/constants';
export * from './shared/utils/validation';
export * from './shared/utils/formatting';
export * from './shared/utils/env';

// Utils that templates expect
export {
  formatCurrency,
  formatDate,
  calculateTax,
  formatTaxRate,
  formatAmountWithTax,
} from './shared/utils/formatting';
export {
  validateCard,
  validateEmail,
  validateAmount,
  validatePricingPlan,
  validatePaymentsConfig,
} from './shared/utils/validation';

// Server-safe provider exports (no React components)
export { PaymentProviderFactory } from './providers';
export * from './providers/stripe';
export * from './providers/mock';

// New providers (temporarily commented out due to build issues)
// export * from './providers/paypal';
// export * from './providers/applepay';

// Platform-specific exports are handled by package.json exports field
// Users should import from:
// - '@digilogiclabs/saas-factory-payments/web' for web components and hooks
// - '@digilogiclabs/saas-factory-payments/native' for React Native components
// - '@digilogiclabs/saas-factory-payments/server' for server-side utilities

// Note: React components and hooks have been moved to platform-specific exports
// to prevent Turbopack bundling conflicts in server-side imports
